import { ThemeOptions } from "@mui/material";
import { menuClasses } from "@mui/material/Menu";
import { BROWSER_AUTOCOMPLETE } from "config/browserAutocomplete";

declare module "@mui/material/styles" {
  interface Palette {
    custom: {
      amberHover: string;
      amberActive: string;
      amberDisabled: string;
      greyLight: string;
      greyBorder: string;
      paperBorder: string;
    };
  }
  interface PaletteOptions {
    custom?: Palette["custom"];
  }
  interface TypographyVariants {
    value1: React.CSSProperties;
    value2: React.CSSProperties;
    heading3: React.CSSProperties;
    heading4: React.CSSProperties;
    label1: React.CSSProperties;
    label1Light: React.CSSProperties;
    label2: React.CSSProperties;
    label2Light: React.CSSProperties;
    label2Regular: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    value1?: React.CSSProperties;
    value2?: React.CSSProperties;
    heading3?: React.CSSProperties;
    heading4?: React.CSSProperties;
    label1?: React.CSSProperties;
    label1Light?: React.CSSProperties;
    label2?: React.CSSProperties;
    label2Light?: React.CSSProperties;
    label2Regular?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    value1: true;
    value2: true;
    heading3: true;
    heading4: true;
    label1: true;
    label1Light: true;
    label2: true;
    label2Light: true;
    label2Regular: true;
  }
}

interface IThemeProps {
  isAdminPage: boolean;
}

const CUSTOM_COLORS = {
  amberHover: "#D79B47",
  amberActive: "#C99142",
  amberDisabled: "#EAC692",
  greyLight: "#E3E3E3",
  greyBorder: "#D3D3D3",
  paperBorder: "#DBE2EA",
};

const FONT_FAMILY = "'Open Sans', sans-serif";

const baseTheme = {
  typography: {
    fontFamily: FONT_FAMILY,
    fontSize: 16,
    value1: {
      fontFamily: FONT_FAMILY,
      fontSize: "32px",
      fontWeight: 700,
      lineHeight: "100%",
      letterSpacing: "0px",
      fontVariantNumeric: "lining-nums tabular-nums",
    },
    value2: {
      fontFamily: FONT_FAMILY,
      fontSize: "20px",
      fontWeight: 600,
      lineHeight: "100%",
      letterSpacing: "0px",
      fontVariantNumeric: "lining-nums tabular-nums",
    },
    heading3: {
      fontFamily: FONT_FAMILY,
      fontSize: "18px",
      fontWeight: 600,
      lineHeight: "24px",
      letterSpacing: "0px",
      fontVariantNumeric: "lining-nums tabular-nums",
    },
    heading4: {
      fontFamily: FONT_FAMILY,
      fontSize: "16px",
      fontWeight: 600,
    },
    label1: {
      fontFamily: FONT_FAMILY,
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "140%",
    },
    label1Light: {
      fontFamily: FONT_FAMILY,
      fontSize: "12px",
      fontWeight: 300,
      lineHeight: "140%",
    },
    label2: {
      fontFamily: FONT_FAMILY,
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "140%",
    },
    label2Light: {
      fontFamily: FONT_FAMILY,
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "140%",
    },
    label2Regular: {
      fontFamily: FONT_FAMILY,
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "100%",
      letterSpacing: "0px",
      fontVariantNumeric: "lining-nums tabular-nums",
    },
  },
  palette: {
    common: {
      white: "#FFFFFF",
    },
    primary: {
      main: "#3D3D3D",
      dark: "#4F5463",
      light: "#F9F9F9",
    },
    text: {
      primary: "#3D3D3D",
      secondary: "#BFBFBF",
    },
    secondary: {
      main: "#E1A34B",
    },
    error: {
      main: "#DE3535",
    },
    custom: CUSTOM_COLORS,
  },
};

export const theme = ({ isAdminPage }: IThemeProps) =>
  ({
    ...baseTheme,

    components: {
      MuiTextField: {
        defaultProps: {
          autoComplete: BROWSER_AUTOCOMPLETE.disabled,
        },
        styleOverrides: {
          root: {
            "& .Mui-error": {
              color: "#DE3535",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: CUSTOM_COLORS.greyBorder,
            },
            "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: CUSTOM_COLORS.greyBorder,
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          listbox: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            fontFamily: "'Open Sans', sans-serif",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            height: "44px",
            textTransform: "none",
            borderRadius: 6,
            fontSize: "16px",
            fontWeight: "600",
            color: "#FFFFFF",

            "& .MuiTypography-root": {
              color: "#FFFFFF",
            },

            "&:hover": {
              backgroundColor: CUSTOM_COLORS.amberHover,
              color: "#FFFFFF",
            },
            "&:active": {
              backgroundColor: CUSTOM_COLORS.amberActive,
              color: "#FFFFFF",
            },
            "&:disabled": {
              backgroundColor: CUSTOM_COLORS.amberDisabled,
              color: "#FFFFFF",
            },
          },
          textSizeLarge: {
            padding: "8px 21px",
          },
          sizeSmall: {
            height: "34px",
            padding: "9px 16px",
            fontSize: "12px",
          },
          containedPrimary: {
            color: "white",
          },
        },
        variants: [
          {
            props: { classes: "mainIcon" },
            style: {
              backgroundColor: "#E1A34B",
              color: "#FFFFFF",
              fill: "#FFFFFF",

              "& .MuiTypography-root": {
                color: "#FFFFFF",
              },

              "&:hover": {
                backgroundColor: CUSTOM_COLORS.amberHover,
                color: "#FFFFFF",
              },
              "&:active": {
                backgroundColor: CUSTOM_COLORS.amberActive,
                color: "#FFFFFF",
              },

              "&:disabled": {
                backgroundColor: "#F9F9F9",
                color: "#BFBFBF",
                fill: "#BFBFBF",

                "& .MuiTypography-root": {
                  color: "#BFBFBF",
                },
              },
            },
          },
          {
            props: { classes: "dark" },
            style: {
              backgroundColor: "#4F5463",
              color: "#FFFFFF",
              fill: "#FFFFFF",

              "& .MuiTypography-root": {
                color: "#FFFFFF",
              },

              "&:hover": {
                backgroundColor: "#3D3D3D",
                color: "#FFFFFF",
                fill: "#FFFFFF",
              },

              "&:active": {
                backgroundColor: "#3D3D3D",
                color: "#FFFFFF",
                fill: "#FFFFFF",
              },

              "&:disabled": {
                backgroundColor: "#F9F9F9",
                color: "#BFBFBF",
                fill: "#BFBFBF",

                "& .MuiTypography-root": {
                  color: "#BFBFBF",
                },
              },
            },
          },
          {
            props: { classes: "light" },
            style: {
              backgroundColor: CUSTOM_COLORS.greyLight,
              color: "#3D3D3D",

              "& .MuiTypography-root": {
                color: "#3D3D3D",
              },

              "&:hover": {
                backgroundColor: CUSTOM_COLORS.greyBorder,
                color: "#3D3D3D",
              },

              "&:active": {
                backgroundColor: CUSTOM_COLORS.greyBorder,
                color: "#3D3D3D",
              },

              "&:disabled": {
                backgroundColor: "#F9F9F9",
                color: "#BFBFBF",
                fill: "#BFBFBF",

                "& .MuiTypography-root": {
                  color: "#BFBFBF",
                },
              },
            },
          },
          {
            props: { classes: "outlined" },
            style: {
              backgroundColor: "#FFFFFF",
              color: "#3D3D3D",
              border: `1px solid ${CUSTOM_COLORS.greyLight}`,

              "& .MuiTypography-root": {
                color: "#3D3D3D",
              },

              "&:hover": {
                backgroundColor: "#F9F9F9",
                color: "#3D3D3D",
              },

              "&:active": {
                backgroundColor: "#F9F9F9",
                color: "#3D3D3D",
              },

              "&:disabled": {
                backgroundColor: "#F9F9F9",
                color: "#BFBFBF",
                fill: "#BFBFBF",
                border: `1px solid ${CUSTOM_COLORS.greyLight}`,

                "& .MuiTypography-root": {
                  color: "#BFBFBF",
                },

                "& *": {
                  fill: "#BFBFBF",
                },
              },
            },
          },
          {
            props: { classes: "icon" },
            style: {
              backgroundColor: "#F9F9F9",
              fill: "#3D3D3D",

              "&:hover": {
                backgroundColor: CUSTOM_COLORS.greyLight,
                fill: "#3D3D3D",
              },

              "&:active": {
                backgroundColor: CUSTOM_COLORS.greyLight,
                fill: "#3D3D3D",
              },

              "&:disabled": {
                backgroundColor: "#F9F9F9",
                fill: "#BFBFBF",
              },
            },
          },
        ],
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            background: "none",
            color: "rgb(65,68,79)",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: "rgb(245, 246, 249)",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&.MuiTableRow-head": {
              backgroundColor: "#F9F9F9",
            },
          },
          head: {
            backgroundColor: "#FFF",
            "& .MuiTableCell-root": {
              backgroundColor: "#F9F9F9",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
          },
          root: {
            fontSize: 16,
            lineHeight: 1.2,
            padding: "6px 12px",
            maxHeight: "45px",
            letterSpacing: "0.02em",
            border: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderRadius: "6px",
            border: "1px solid rgba(219, 226, 234, 1)",
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          page: {
            fontSize: 12,
            color: "rgb(131,131,131)",
            "&.selected": {
              backgroundColor: "#EBEBEB",
              color: "#FFFFFF",
              fontWeight: 500,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            fontWeight: 500,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            border: "none",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {},
      },
      MuiMenu: {
        styleOverrides: {
          list: {
            [`.${menuClasses.root}[aria-hidden] &`]: {
              pointerEvents: "none",
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row",
            padding: "24px",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            transition: "opacity .3s",
            transform: "none",
            boxShadow: "0 0 15px rgba(0, 0, 0, .1)",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            marginBottom: "6px",
            display: "block",
            fontSize: "12px",
            fontWeight: "600",
            color: "black",
            "&.MuiInputLabel-root": {},
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            fontSize: "14px",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {},
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            height: 20,
            padding: 0,
            width: 35,
          },
          switchBase: {
            padding: 2,
            margin: 2,
            "&.Mui-checked": {
              transform: "translateX(16px)",
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: baseTheme.palette.secondary.main,
                opacity: 1,
                border: 0,
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.5,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: "#33cf4d",
              border: "6px solid #fff",
            },

            "&.Mui-checked .MuiSwitch-thumb": {
              boxShadow:
                "0px 2px 1px -1px #C18C42, 0px 1px 1px 0px #C18C42, 0px 1px 3px 0px #C18C42",
            },
          },
          track: {
            borderRadius: 26 / 2,
            backgroundColor: "#E9E9EA",
            opacity: 1,
          },
          thumb: {
            boxSizing: "border-box",
            width: 12,
            height: 12,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
            fontSize: 12,
          },
          clickable: {
            fontSize: 12,
            border: `1px solid ${CUSTOM_COLORS.paperBorder}`,
            backgroundColor: "#FAFAFA",
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            label1: "p",
            label1Light: "p",
            label2: "p",
            label2Light: "p",
          },
        },
        styleOverrides: {
          root: {
            minWidth: "auto",
            color: baseTheme.palette.text.primary,
            letterSpacing: "0.03em",
          },
          h1: {
            fontSize: isAdminPage ? "24px" : "34px",
            fontWeight: "600",
            lineHeight: isAdminPage ? "1em" : "1.3em`",

            "@media screen and (max-width: 1000px)": {
              fontSize: "28px",
            },
            "@media screen and (max-width: 600px)": {
              fontSize: "24px",
              lineHeight: "1.4em",
            },
          },
          h2: {
            fontSize: "30px",
            fontWeight: "600",

            "@media screen and (max-width: 1000px)": {
              fontSize: "24px",
            },
          },
          h3: {
            fontSize: "18px",
            fontWeight: "600",
            lineHeight: "1.4em",

            "@media screen and (max-width: 1000px)": {
              fontSize: "16px",
            },
          },
          h4: {
            fontSize: "20px",
            fontWeight: "600",

            "@media screen and (max-width: 600px)": {
              fontSize: "16px",
            },
          },
          h5: {
            fontSize: "14px",
            fontWeight: "300",
            lineHeight: "1.3em",

            "@media screen and (max-width: 600px)": {
              fontSize: "12px",
            },
          },
          subtitle1: {
            fontSize: "18px",
            fontWeight: "300",
            lineHeight: "1.5em",

            "@media screen and (max-width: 1000px)": {
              fontSize: "16px",
            },
          },
          subtitle2: {
            fontSize: "14px",
            fontWeight: "300",
            lineHeight: "1.5em",

            "@media screen and (max-width: 1000px)": {
              fontSize: "16px",
              lineHeight: "1.6em",
            },
          },
          body1: {
            fontSize: "16px",
            fontWeight: "300",
            lineHeight: "1.6em",

            "@media screen and (max-width: 1000px)": {
              fontSize: "14px",
            },
            "@media screen and (max-width: 600px)": {
              fontSize: "16px",
            },
          },
          body2: {
            fontSize: "16px",
            fontWeight: "300",
            lineHeight: "1.6em",

            "@media screen and (max-width: 600px)": {
              fontSize: "12px",
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: 0,

            "&:before": {
              backgroundColor: "white",
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontSize: "14px",
            fontWeight: "300",
          },
          input: {
            padding: "12px 14px",
            fontSize: "14px",
            fontWeight: "300",
          },
        },
      },
    },
  } as ThemeOptions);
